import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MessageCircle, Swords, Heart, Gem, LayoutDashboard, ArrowRight, X, Sparkles } from 'lucide-react';

const TOUR_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to HiBFF!',
    description: 'Your social confidence journey starts here. Let us show you around the key features.',
    icon: Sparkles,
    target: null, // no specific element
    position: 'center',
  },
  {
    id: 'ava',
    title: 'Meet Your AvA',
    description: 'AvA is your AI practice partner. Start conversations to build real-world social skills.',
    icon: MessageCircle,
    target: '[data-testid="sidebar-ava"]',
    fallbackTarget: 'a[href="/conversations"]',
    position: 'right',
  },
  {
    id: 'quests',
    title: 'Social Quests',
    description: 'Multi-session story arcs that guide you through real social scenarios. Complete chapters to earn Hearts and NFTs.',
    icon: Swords,
    target: '[data-testid="sidebar-apps"]',
    fallbackTarget: 'a[href="/quests"]',
    position: 'right',
  },
  {
    id: 'hearts',
    title: 'Hearts Economy',
    description: 'Earn Hearts through conversations and quests. Spend them on collectibles, staking, and limited drops.',
    icon: Heart,
    target: '[data-testid="hearts-balance"]',
    fallbackTarget: '.hearts-balance',
    position: 'bottom',
  },
  {
    id: 'nft',
    title: 'NFT Marketplace',
    description: 'Collect unique digital art as you grow. Mint, trade, and showcase your achievements on the blockchain.',
    icon: Gem,
    target: '[data-testid="sidebar-apps"]',
    fallbackTarget: 'a[href="/nft-marketplace"]',
    position: 'right',
  },
  {
    id: 'dashboard',
    title: 'Your Dashboard',
    description: 'Customise your dashboard with the cards you want. Click "Customise" to add or remove widgets.',
    icon: LayoutDashboard,
    target: '[data-testid="customize-button"]',
    fallbackTarget: null,
    position: 'bottom',
  },
];

const STORAGE_KEY = 'hibff_onboarding_completed';

const OnboardingTour = ({ user }) => {
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const tooltipRef = useRef(null);

  // Check if user has completed the tour
  useEffect(() => {
    if (!user) return;
    const completed = localStorage.getItem(`${STORAGE_KEY}_${user.user_id || user.email}`);
    if (!completed) {
      const timer = setTimeout(() => setActive(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [user]);

  const currentStep = TOUR_STEPS[step];

  const positionTooltip = useCallback(() => {
    if (!currentStep) return;
    if (currentStep.position === 'center' || !currentStep.target) {
      setTooltipStyle({ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
      return;
    }

    const target = document.querySelector(currentStep.target) ||
      (currentStep.fallbackTarget ? document.querySelector(currentStep.fallbackTarget) : null);

    if (!target) {
      setTooltipStyle({ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
      return;
    }

    const rect = target.getBoundingClientRect();
    const tooltip = tooltipRef.current;
    const tooltipW = tooltip?.offsetWidth || 300;
    const tooltipH = tooltip?.offsetHeight || 200;
    const pad = 12;

    let top, left;

    switch (currentStep.position) {
      case 'right':
        top = rect.top + rect.height / 2 - tooltipH / 2;
        left = rect.right + pad;
        if (left + tooltipW > window.innerWidth) {
          left = rect.left - tooltipW - pad;
        }
        break;
      case 'bottom':
        top = rect.bottom + pad;
        left = rect.left + rect.width / 2 - tooltipW / 2;
        break;
      case 'top':
        top = rect.top - tooltipH - pad;
        left = rect.left + rect.width / 2 - tooltipW / 2;
        break;
      default:
        top = rect.bottom + pad;
        left = rect.left;
    }

    // Clamp within viewport
    top = Math.max(8, Math.min(top, window.innerHeight - tooltipH - 8));
    left = Math.max(8, Math.min(left, window.innerWidth - tooltipW - 8));

    setTooltipStyle({ position: 'fixed', top: `${top}px`, left: `${left}px` });
  }, [currentStep]);

  useEffect(() => {
    if (!active) return;
    positionTooltip();
    window.addEventListener('resize', positionTooltip);
    return () => window.removeEventListener('resize', positionTooltip);
  }, [active, step, positionTooltip]);

  const handleNext = () => {
    if (step < TOUR_STEPS.length - 1) {
      setStep(s => s + 1);
    } else {
      completeTour();
    }
  };

  const completeTour = () => {
    if (user) {
      localStorage.setItem(`${STORAGE_KEY}_${user.user_id || user.email}`, 'true');
    }
    setActive(false);
  };

  if (!active) return null;

  const Icon = currentStep.icon;
  const isLast = step === TOUR_STEPS.length - 1;

  return (
    <div className="fixed inset-0 z-[90]" data-testid="onboarding-tour">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={completeTour} />

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        style={tooltipStyle}
        className="w-[300px] sm:w-[340px] bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden z-[91]"
        data-testid={`tour-step-${currentStep.id}`}
      >
        {/* Header accent */}
        <div className="h-1.5" style={{ background: 'linear-gradient(90deg, #F5831F, #FF6B35)' }} />

        <div className="p-5">
          {/* Close */}
          <button
            onClick={completeTour}
            className="absolute top-3 right-3 p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            data-testid="tour-skip"
          >
            <X size={16} />
          </button>

          {/* Icon */}
          <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: '#FFF3EB' }}>
            <Icon size={20} style={{ color: '#F5831F' }} />
          </div>

          {/* Content */}
          <h3 className="text-base font-bold mb-1.5" style={{ color: '#1A1209' }}>{currentStep.title}</h3>
          <p className="text-sm text-gray-500 leading-relaxed mb-4">{currentStep.description}</p>

          {/* Footer */}
          <div className="flex items-center justify-between">
            {/* Progress dots */}
            <div className="flex gap-1.5">
              {TOUR_STEPS.map((_, i) => (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full transition-colors ${i === step ? 'bg-orange-500' : i < step ? 'bg-orange-200' : 'bg-gray-200'}`}
                />
              ))}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={completeTour}
                className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 font-medium transition-colors"
                data-testid="tour-skip-btn"
              >
                Skip
              </button>
              <button
                onClick={handleNext}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-white transition-colors"
                style={{ backgroundColor: '#F5831F' }}
                data-testid="tour-next"
              >
                {isLast ? 'Get Started' : 'Next'}
                <ArrowRight size={12} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
